import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { Subject, takeUntil } from 'rxjs';
import { SettingsApi, Theme, User } from '../../hickory-api';
import { AnalyticsHelper } from '../analytics/analytics-helper';
import { AppVersionService } from '../app-version.service';
import { AuthService } from '../authentication/auth.service';
import { AuthEventsComponent } from '../authentication/auth-events/auth-events.component';
import { AccessControl } from '../authentication/access-control';
import { FeedbackComponent } from '../feedback/feedback.component';
import { LogHelper } from '../logging/log-helper';
import { NavService, NavigationElement } from '../navigation/nav.service';
import { ViewState } from '../responsive.service';
import { StorageHelper } from '../storage/storage-helper';
import { LayoutService } from './layout.service';
import { FilesViewConstants } from 'src/app/files/file-view-context';
import { MfaService } from 'src/app/user/mfa.service';
import { NotificationService } from '../notifications/notification.service';
import { ThemeService } from '../theme/theme.service';
import { AppTheme } from '../theme/app-theme';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss', '../styles/colors.scss', '../styles/globals.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly settingsApi = inject(SettingsApi);
  private readonly navService = inject(NavService);
  private readonly layoutService = inject(LayoutService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly translation = inject(L10nTranslationService);
  private readonly dialog = inject(MatDialog);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly mfaService = inject(MfaService);
  private readonly notificationService = inject(NotificationService);
  private readonly themeService = inject(ThemeService);

  @ViewChild('navDrawer') navDrawer: MatSidenav;
  @ViewChild('changeSupportedFeatures') changeSupportedFeatures: TemplateRef<unknown>;

  currentPage: string;
  user: User;
  loadingRouteConfig: boolean = false;
  isBetaUx: boolean = false;
  isBickory: boolean = false;
  isBetaExit: boolean = false;

  private readonly themeSettings = computed<AppTheme>(() => this.themeService.logoAndColors())
  logoUri = computed(() => this.themeSettings().logoUrl);
  primaryColor = computed(() => this.themeSettings().primaryColor);
  secondaryColor = computed(() => this.themeSettings().secondaryColor);
  supportInfo = computed(() => this.themeSettings()?.internalSupport);
  get navElements(): NavigationElement[] { return this.navService.getNavigation().elements; }
  get isSuperAdmin(): boolean { return this.authService.isSuperAdmin(); }
  get canImpersonate(): boolean { return this.isSuperAdmin; }
  get isImpersonating(): boolean { return this.authService.isImpersonating(); }
  get viewState(): ViewState { return this.layoutService.responsiveViewState; }
  get toolbarHeight(): number { return this.hideFraming ? 0 : this.layoutService.layoutHeightOffsetTop; }

  drawerOpenInitial = true;
  // for the FilePickerComponent (and potentially others) -- hide the toolbar and sidenav
  hideFraming: boolean = false;

  private readonly log = new LogHelper('Layout');
  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    AnalyticsHelper.addAttribute('appVersion', this.appVersionService.detectAppVersion());

    // apply cached theme, if present
    this.applyTheme(StorageHelper.getTheme());

    // Set the initial drawer state to open for desktop, closed for mobile
    this.drawerOpenInitial = this.viewState.isLargeIsh;

    // listen for layout changes
    this.layoutService.layoutInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => this.changeDetectorRef.markForCheck());

    // Wire into events
    this.authService.user.data.pipe(takeUntil(this.destroy$)).subscribe((user) => this.onUserChanged(user));

    // Update page name when nav info changes
    this.layoutService.navInfo$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
      if (info) {
        this.currentPage = info.name;
        AnalyticsHelper.updatePageTitle(this.translation.translate(this.currentPage ?? 'Unknown Page'));
      }
    });

    // see: https://stackoverflow.com/q/42981136/1363731
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event.type === EventType.RouteConfigLoadStart) {
        this.loadingRouteConfig = true;
      } else if (event.type === EventType.RouteConfigLoadEnd) {
        this.loadingRouteConfig = false;
      } else if (event.type === EventType.NavigationEnd) {
        AnalyticsHelper.logPageView();
        this.hideFraming = event?.url?.includes(FilesViewConstants.RTE_ROUTE) === true;
        if (event?.url?.includes('navigation=collapsed') === true) {
          this.drawerOpenInitial = false;
        }
      }
    });

    // Check for Beta experience
    this.isBetaUx = StorageHelper.isBetaUx();
    if (!this.isBetaUx) {
      const rx$ = this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.isBetaUx = !!params[NavService.betaQueryParam];
        if (this.isBetaUx) {
          // Save to cookie to survive auth redirects
          StorageHelper.saveIsBetaUx(true);
          // No longer need to listen to query
          rx$.unsubscribe();
        }
      });
    }

    this.themeService.forceThemeUpdate.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      void this.onUserChanged(this.user);
    });
  }

  ngAfterViewInit(): void {
    if (this.navDrawer) {
      this.navDrawer.openedChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
        globalThis.dispatchEvent(new Event('resize'));
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  drawerOpenInitialValue() {
    return this.navDrawer?.opened ?? this.drawerOpenInitial;
  }

  isLoginPage() {
    return this.currentPage === 'Login';
  }

  showFeedback() {
    this.dialog.open(FeedbackComponent, { minWidth: '600px', data: { isBeta: this.isBetaExit }, autoFocus: false });
  }

  leaveBeta() {
    this.isBetaExit = true;
    this.showFeedback();
  }

  onTitleLongPress() {
    this.log.debug('onTitleLongPress');
    void this.authService.refreshAccessToken();

    AnalyticsHelper.logEvent({
      action: 'nav-title-long-press',
      name: 'LongPress',
    });
  }

  onViewAuthLog() {
    this.dialog.open(AuthEventsComponent, { minWidth: '600px' });
  }

  onRouteClick() {
    if (this.navDrawer.mode === 'over' && this.navDrawer.opened) {
      void this.navDrawer.close();
    }
  }

  applyTheme(theme?: Theme) {
    this.themeService.setAppThemeSettings(theme);
    this.changeDetectorRef.markForCheck();
  }

  private async onUserChanged(user: User) {
    this.user = user;
    this.applyTheme(this.themeService.userTheme());

    if (this.user?.permissions?.includes(AccessControl.FeatureChatbotForethought)) {
      this.loadForethoughtChatbot();
    }

    if (this.user) {
      this.applyNavRules(user);

      AnalyticsHelper.updateUser(user);

      try {
        await this.notificationService.load();
        await this.mfaService.syncStatus();
      } catch (err) {
        this.log.warn('notification/mfa init failed', err);
      }

      this.settingsApi
        .getTheme()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          const theme = data?.item;
          if (theme) {
            StorageHelper.saveTheme(theme);
            this.applyTheme(theme);
          }

          this.layoutService.startPollingBannerConfig(theme?.brandName);
        });
    }
  }

  private applyNavRules(user: User) {
    if (this.authService.isAdminUser(user)) {
      this.navService.setStandardNavigation();
    } else {
      this.navService.setNonAdminNavigation();
    }

    if (this.user.tenantId === 0) {
      this.navService.appendBackOfficeNavigation();
    }
  }

  private loadForethoughtChatbot(): void {
    const scriptId = 'forethought-widget-embed-script';

    // Check if already loaded
    if (document.getElementById(scriptId)) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://solve-widget.forethought.ai/embed.js';
    script.id = scriptId;

    script.setAttribute('initial-intent-id', '');
    script.setAttribute('config-ft-placement', 'bottom_right');
    script.dataset.apiKey = 'c9c31f19-2cc5-4bd1-a1c4-64de706ada6c';
    script.dataset.ftEmbedScriptLanguage = 'English';
    script.dataset.ftWorkflowTag = 'Mass Notification';
    script.dataset.ftEmail = '';

    script.onerror = (err) => {
      this.log.debug('Forethought chatbot: failed to load', err);
    };

    script.onload = () => {
      this.log.debug('Forethought chatbot: loaded');
    };

    document.body.appendChild(script);
  }
}
